import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { FileUploader } from "@/components/common/FileUploader";
import { fileService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Trash2, HardDrive } from "lucide-react";

function formatBytes(b = 0) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FilesPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [used, setUsed] = useState(0);
  const canDelete = hasPermission("FILE.DELETE");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fileService.list({ size: 100 });
      setRows(d.items || []);
      setUsed(d.usedBytes || 0);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const download = async (row) => {
    try {
      const res = await fileService.download(row.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = row.fileName || "file";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.fileName}?`)) return;
    try {
      await fileService.remove(row.id);
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    }
  };

  const columns = [
    { key: "fileName", header: "File", render: (r) => <span className="font-medium text-foreground">{r.fileName}</span> },
    { key: "category", header: "Category", render: (r) => <Badge color="teal">{r.category}</Badge> },
    { key: "mimeType", header: "Type", render: (r) => <span className="text-muted-foreground text-xs">{r.mimeType || "—"}</span> },
    { key: "size", header: "Size", render: (r) => formatBytes(r.size) },
    { key: "storageProvider", header: "Storage", render: (r) => <Badge color={r.storageProvider === "s3" ? "blue" : "slate"}>{r.storageProvider}</Badge> },
    { key: "createdOn", header: "Uploaded", render: (r) => (r.createdOn || "").slice(0, 10) },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => download(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`download-${r.id}`}>
            <Download className="h-4 w-4" />
          </button>
          {canDelete && (
            <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`delete-${r.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="files-page">
      <PageHeader
        title="File Manager"
        subtitle="Tenant-isolated object storage (AWS S3 in production)"
        actions={hasPermission("FILE.UPLOAD") && <FileUploader category="general" onUploaded={load} />}
      />
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4" data-testid="storage-used">
        <HardDrive className="h-4 w-4" /> Storage used: <span className="font-semibold text-foreground">{formatBytes(used)}</span>
      </div>
      <DataTable columns={columns} rows={rows} loading={loading} testid="files-table" empty="No files uploaded yet" />
    </div>
  );
}
